import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  Shield, 
  Lock, 
  Plug, 
  FileCheck, 
  Smartphone, 
  Heart,
  ArrowRight,
  Check,
  Sparkles
} from 'lucide-react'
import GraffitiLogo from './GraffitiLogo'

const features = [
  {
    icon: Lock,
    title: 'Fully Private & Localized',
    description: 'Operating system runs entirely on your device',
  },
  {
    icon: Shield,
    title: 'RSA 2048 Encryption',
    description: 'Military-grade security for all communications',
  },
  {
    icon: Plug,
    title: 'Plug + Play',
    description: 'Simple adoptions & activations out of the box',
  },
  {
    icon: FileCheck,
    title: 'Compliance-Minded Design',
    description: 'PCI-DSS, HIPAA, and PII rules built into the governance layer',
  },
  {
    icon: Smartphone,
    title: 'Extendible',
    description: 'Connect to IoT, iPhone App, or syndicated networks',
  },
  {
    icon: Heart,
    title: 'Your Rules',
    description: 'Lives by YOUR rules, to help YOU',
  },
]

export default function ScramblerShowcase() {
  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-950/30 via-void-950 to-synth-950/20" />
      
      {/* Decorative elements */}
      <div className="absolute top-1/4 left-0 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-synth-600/10 rounded-full blur-3xl" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <GraffitiLogo className="h-14 w-14" />
            <div className="text-left">
              <h2 className="text-4xl md:text-5xl font-display font-bold">
                <span className="text-purple-400">Graffiti™</span>
                <span className="text-void-200 font-normal ml-3">Scrambler</span>
              </h2>
            </div>
          </div>
          <p className="text-xl text-void-400 max-w-2xl mx-auto">
            Your personal AI client for connection to models, agents, and agent networks
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Product Image / Visualization */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="aspect-square max-w-lg mx-auto relative">
              {/* Device visualization */}
              <div className="absolute inset-0 flex items-center justify-center">
                {/* Glow effect */}
                <div className="absolute w-72 h-72 bg-purple-600/20 rounded-full blur-3xl" />
                
                {/* Device body */}
                <div className="relative">
                  {/* Main body - speaker shape */}
                  <div className="w-56 h-72 bg-gradient-to-b from-purple-600 via-purple-700 to-purple-800 rounded-[2rem] shadow-2xl shadow-purple-900/50 relative overflow-hidden">
                    {/* Screen area */}
                    <div className="absolute top-6 left-1/2 -translate-x-1/2 w-36 h-36 bg-void-900 rounded-2xl border-4 border-purple-500/30 overflow-hidden">
                      {/* Screen content - Avatar */}
                      <div className="w-full h-full bg-gradient-to-b from-purple-900/50 to-void-950 flex items-center justify-center">
                        {/* Simple robot face */}
                        <div className="text-center">
                          <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-gradient-to-b from-cyan-400 to-cyan-600 flex items-center justify-center">
                            {/* Eyes */}
                            <div className="flex gap-3">
                              <div className="w-3 h-3 bg-void-900 rounded-full" />
                              <div className="w-3 h-3 bg-void-900 rounded-full" />
                            </div>
                          </div>
                          {/* Mouth/expression */}
                          <div className="w-8 h-2 mx-auto bg-cyan-400 rounded-full" />
                        </div>
                      </div>
                      
                      {/* Status bar */}
                      <div className="absolute top-2 left-2 right-2 flex justify-between text-[8px] text-purple-300">
                        <span>●●●</span>
                        <span>ONLINE</span>
                      </div>
                    </div>
                    
                    {/* Speaker grille */}
                    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-32">
                      <div className="flex flex-col gap-1.5">
                        {[...Array(5)].map((_, i) => (
                          <div key={i} className="h-1 bg-purple-900/50 rounded-full" />
                        ))}
                      </div>
                    </div>
                    
                    {/* LED indicator */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
                      <motion.div
                        animate={{ opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="w-2 h-2 bg-cyan-400 rounded-full shadow-lg shadow-cyan-400/50"
                      />
                    </div>
                    
                    {/* Graffiti™ logo on device */}
                    <div className="absolute top-48 left-1/2 -translate-x-1/2">
                      <GraffitiLogo className="h-8 w-8" animated={false} />
                    </div>
                  </div>
                  
                  {/* Base/stand */}
                  <div className="w-64 h-4 bg-gradient-to-b from-purple-800 to-purple-900 rounded-full mx-auto -mt-2 shadow-xl" />
                </div>
              </div>
              
              {/* Floating feature badges */}
              <motion.div
                animate={{ y: [-5, 5, -5] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="absolute top-8 right-4 px-3 py-1.5 bg-void-900/80 backdrop-blur-sm rounded-full border border-purple-500/30 text-xs text-purple-300"
              >
                <span className="flex items-center gap-1.5">
                  <Lock className="w-3 h-3" />
                  RSA 2048
                </span>
              </motion.div>
              
              <motion.div
                animate={{ y: [5, -5, 5] }}
                transition={{ duration: 4, repeat: Infinity, delay: 1 }}
                className="absolute bottom-16 left-4 px-3 py-1.5 bg-void-900/80 backdrop-blur-sm rounded-full border border-cyan-500/30 text-xs text-cyan-300"
              >
                <span className="flex items-center gap-1.5">
                  <Shield className="w-3 h-3" />
                  HIPAA Safe
                </span>
              </motion.div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            {/* Tagline */}
            <div className="mb-8">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-900/30 border border-purple-700/30 text-purple-300 text-sm font-medium mb-4">
                <Sparkles className="w-4 h-4" />
                Hardware + AI Client
              </span>
              <h3 className="text-3xl md:text-4xl font-display font-bold text-void-50 mb-4">
                We Come in Peace.
              </h3>
              <p className="text-lg text-void-300 leading-relaxed">
                The future of Artificial Intelligence depends on our ability to access its 
                capabilities without sacrificing our privacy and security. The feature-rich 
                Graffiti™ Scrambler provides an inexpensive entry to functional and ethical 
                use of language models.
              </p>
            </div>

            {/* Features list */}
            <div className="mb-8">
              <h4 className="text-sm font-semibold text-purple-400 uppercase tracking-wider mb-4">
                Immediately Impactful Features
              </h4>
              <div className="grid sm:grid-cols-2 gap-3">
                {features.map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-3 p-3 rounded-xl bg-void-900/30 border border-void-800/50"
                  >
                    <div className="p-1.5 rounded-lg bg-purple-900/50 text-purple-400">
                      <feature.icon className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-void-200">{feature.title}</p>
                      <p className="text-xs text-void-500">{feature.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Marketplace callout */}
            <div className="p-4 rounded-xl bg-gradient-to-r from-purple-900/30 to-synth-900/30 border border-purple-800/30 mb-8">
              <p className="text-void-300 text-sm">
                The <span className="text-purple-400 font-semibold">case marketplace</span> provides 
                several enhanced extensions which can be easily adopted and integrated, all operating 
                in a responsible environment.
              </p>
            </div>

            {/* CTA */}
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/products/home/demo" 
                className="btn-primary flex items-center gap-2 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600"
              >
                <Sparkles className="w-4 h-4" />
                Try Voice Demo
              </Link>
              <Link 
                to="/products/home" 
                className="btn-secondary flex items-center gap-2"
              >
                Learn More
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Bottom stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6"
        >
          {[
            { label: 'Data Residency', value: 'By Design', sublabel: 'Not By Contract' },
            { label: 'Encryption', value: 'RSA 2048', sublabel: 'In Transit & At Rest' },
            { label: 'Compliance', value: 'Rulebook', sublabel: 'PCI-DSS, HIPAA, PII' },
            { label: 'Setup', value: 'One Person', sublabel: 'No Engineering Team' },
          ].map((stat, index) => (
            <div key={stat.label} className="text-center p-4 rounded-xl bg-void-900/30 border border-void-800/50">
              <p className="text-2xl md:text-3xl font-display font-bold text-purple-400">{stat.value}</p>
              <p className="text-sm text-void-300">{stat.label}</p>
              <p className="text-xs text-void-500">{stat.sublabel}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}